import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../features/auth'
import {
  AppointmentDepartureReminderModal,
  DepartureReminderModal,
} from '../../features/aufgaben/departure-reminder'
import { UnclosedItemsReminderModal } from '../../features/aufgaben/unclosed-reminder'
import { useWorkerCalendar } from '../../entities/appointment'
import { useTechnicianTasks } from '../../entities/task'
import { WorkerCalendarWidget } from '../../widgets/worker-calendar'
import { useWorkerCalendarDays } from '../../widgets/worker-calendar/model/useWorkerCalendarDays'
import { PATHS } from '../../app/routes/paths'
import { routes } from '../../shared/config/routes'
import { isSameDay } from '../../shared/helpers/date'

export const WorkerCalendarPage = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { days, dayIsos, from, to, todayIso, prev, next, goToToday } = useWorkerCalendarDays()

  const { data, isFetching, refetch: refetchCalendar} = useWorkerCalendar(user?.id, { from, to })
  const { data: tasksData, isFetching: isTasksFetching } = useTechnicianTasks(user?.id, { from, to })
  const isTransitioning = isFetching || isTasksFetching

  const [departureTaskId, setDepartureTaskId] = useState<number | null>(null)
  const [departureAppointmentId, setDepartureAppointmentId] = useState<string | null>(null)
  const [showUnclosedModal, setShowUnclosedModal] = useState(false)
  const unclosedDismissedRef = useRef(false)

  const scheduledTasks = tasksData?.scheduledTasks ?? []

  const now = new Date()

  const isOverduePlanned = (
    status: string,
    plannedStart: string | null | undefined,
    travel: number,
    work: number
  ) => {
    if (status !== 'PLANNED' || !plannedStart) return false
    if (!isSameDay(plannedStart, now)) return false
    const end = new Date(plannedStart).getTime() + (travel + work) * 60_000
    return end < now.getTime()
  }

  const isStaleInProgress = (
    status: string,
    actualStart: string | null | undefined,
    actualEnd: string | null | undefined
  ) => {
    if (status !== 'PLANNED') return false
    if (!actualStart || actualEnd) return false
    return !isSameDay(actualStart, now)
  }

  const unclosedAppointments = (data?.appointments ?? []).filter((a) =>
    isOverduePlanned(a.status, a.plannedStart, a.plannedTravelDuration, a.plannedWorkDuration) ||
    isStaleInProgress(a.status, a.actualStart, a.actualEnd)
  )

  const unclosedTasks = scheduledTasks.filter((t) =>
    isOverduePlanned(t.status, t.plannedStart ?? undefined, t.plannedTravelDuration, t.plannedWorkDuration) ||
    isStaleInProgress(t.status, t.actualStart ?? undefined, t.actualEnd ?? undefined)
  )

  const departureReminderTasks = scheduledTasks.filter(
    (t) => t.status === 'COMPLETED' && t.actualDepartureDuration == null && t.plannedDepartureDuration > 0
  )

  const departureTask = departureReminderTasks.find((t) => t.id === departureTaskId)

  const appointmentDepartureReminderItems = (data?.appointments ?? []).filter(
    (a) => a.status === 'COMPLETED' && a.actualDepartureDuration == null && a.plannedDepartureDuration > 0
  )

  const departureAppointment = appointmentDepartureReminderItems.find((a) => a.id === departureAppointmentId)

  const handleAppointmentClick = (appointmentId: string) => {
    navigate(PATHS.workerAppointment.replace(':appointmentId', appointmentId))
  }

  const handleTMBlockClick = (entryId: number) => {
    navigate(routes.workerZeitmanagement.edit(entryId))
  }

  const handleTaskClick = (taskId: number) => {
    navigate(PATHS.workerTask.replace(':taskId', String(taskId)))
  }

  const handleToday = () => {
    goToToday()
    refetchCalendar()
  }

  useEffect(() => {
    if (unclosedDismissedRef.current) return
    if (unclosedAppointments.length === 0 && unclosedTasks.length === 0) return
    setShowUnclosedModal(true)
  }, [unclosedAppointments.length, unclosedTasks.length])

  const handleCloseUnclosedModal = (open: boolean) => {
    if (!open) {
      unclosedDismissedRef.current = true
      setShowUnclosedModal(false)
    }
  }

  const handleUnclosedAppointmentClick = (id: string) => {
    unclosedDismissedRef.current = true
    setShowUnclosedModal(false)
    handleAppointmentClick(id)
  }

  const handleUnclosedTaskClick = (id: number) => {
    unclosedDismissedRef.current = true
    setShowUnclosedModal(false)
    handleTaskClick(id)
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <WorkerCalendarWidget
        days={days}
        dayIsos={dayIsos}
        todayIso={todayIso}
        onPrev={prev}
        onNext={next}
        onToday={handleToday}
        technicianData={data ?? null}
        currentUserId={Number(user?.id ?? 0)}
        onAppointmentClick={handleAppointmentClick}
        onTMBlockClick={handleTMBlockClick}
        scheduledTasks={scheduledTasks}
        unscheduledTasks={tasksData?.unscheduledTasks ?? []}
        onTaskClick={handleTaskClick}
        departureReminderTasks={departureReminderTasks}
        onDepartureTaskClick={setDepartureTaskId}
        appointmentDepartureReminderItems={appointmentDepartureReminderItems}
        onAppointmentDepartureClick={setDepartureAppointmentId}
        isTransitioning={isTransitioning}
      />

      {departureTask && (
        <DepartureReminderModal
          show={departureTaskId !== null}
          setShow={(open) => { if (!open) setDepartureTaskId(null) }}
          taskId={departureTask.id}
          plannedDepartureDuration={departureTask.plannedDepartureDuration}
          onSuccess={() => setDepartureTaskId(null)}
        />
      )}

      {departureAppointment && (
        <AppointmentDepartureReminderModal
          show={departureAppointmentId !== null}
          setShow={(open) => { if (!open) setDepartureAppointmentId(null) }}
          appointment={departureAppointment}
          onSuccess={() => setDepartureAppointmentId(null)}
        />
      )}

      <UnclosedItemsReminderModal
        show={showUnclosedModal}
        setShow={handleCloseUnclosedModal}
        appointments={unclosedAppointments}
        tasks={unclosedTasks}
        onAppointmentClick={handleUnclosedAppointmentClick}
        onTaskClick={handleUnclosedTaskClick}
      />
    </div>
  )
}
